import * as React from 'react';

import { ExpressionError, ExpressionRuntime, SymbolsTable } from '@gitbook/expr';

export type ExpressionContextAutocompleteFilter = (
    symbolPath: string | string[]
) => boolean | ({ exclusionMessage: React.ReactNode } | undefined);

export type ExpressionContextValue = {
    /**
     * The runtime to evaluate the expressions.
     */
    runtime: ExpressionRuntime;
    /**
     * The symbols table to resolve the variables.
     */
    symbolsTable: SymbolsTable;
    /**
     * The input values to provide to the expressions.
     */
    inputValues: Record<string, unknown>;

    /**
     * Optional filter to apply the autocomplete suggestion.
     */
    autocompleteFilter?: ExpressionContextAutocompleteFilter;
};

export interface DynamicPropChecker {
    /**
     * Check whether the given symbol path refers to a dynamic property.
     * The path should match the format used by the SymbolsTable (e.g., "visitor.claims.foo").
     */
    isDynamicProperty: (symbolPath: string | string[]) => boolean;
}

const ExpressionContext = React.createContext<ExpressionContextValue>({
    runtime: new ExpressionRuntime(),
    symbolsTable: new SymbolsTable(),
    inputValues: {},
    autocompleteFilter: undefined,
});

export type ExpressionEvaluationResult =
    | {
          value: unknown;
          error?: undefined;
      }
    | {
          value?: undefined;
          error: ExpressionError;
      };

/**
 * Extend the evaluation context.
 */
export function ExpressionProvider(
    props: React.PropsWithChildren<{
        symbolsTable?: SymbolsTable;
        inputValues?: Record<string, unknown>;
        autocompleteFilter?: ExpressionContextAutocompleteFilter;
    }>
) {
    const { inputValues, symbolsTable, autocompleteFilter, children } = props;
    const parentContext = React.useContext(ExpressionContext);

    const context: ExpressionContextValue = React.useMemo(() => {
        return {
            runtime: parentContext.runtime,
            symbolsTable: symbolsTable
                ? parentContext.symbolsTable.merge(symbolsTable)
                : parentContext.symbolsTable,
            inputValues: {
                ...parentContext.inputValues,
                ...inputValues,
            },
            autocompleteFilter: parentContext.autocompleteFilter ?? autocompleteFilter,
        };
    }, [parentContext, inputValues, symbolsTable, autocompleteFilter]);

    return <ExpressionContext.Provider value={context}>{children}</ExpressionContext.Provider>;
}

/**
 * Get the current context.
 */
export function useExpressionContext(): ExpressionContextValue {
    return React.useContext(ExpressionContext);
}

/**
 * Evaluate an expression.
 */
export function useEvaluateExpression(expr: string): ExpressionEvaluationResult {
    const context = React.useContext(ExpressionContext);

    return React.useMemo(() => {
        try {
            const value = context.runtime.evaluate(expr, context.inputValues);
            return { value };
        } catch (error) {
            if (error instanceof ExpressionError) {
                return {
                    error,
                };
            }
            throw error;
        }
    }, [context.runtime, context.inputValues, expr]);
}
